import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // You can also log the error to an error reporting service here
    // logErrorToService(error, errorInfo);
  }

  private handleReload = () => {
    // Soft recovery: try to reset app state instead of hard reload
    try {
      // Clear error state and attempt recovery
      this.setState({ hasError: false, error: undefined, errorInfo: undefined });
      
      // If still having issues after soft recovery, then do hard reload as last resort
      setTimeout(() => {
        if (this.state.hasError) {
          console.warn('🔄 Soft recovery failed, performing hard reload as last resort');
          window.location.reload();
        }
      }, 1000);
    } catch (recoveryError) {
      console.error('Recovery attempt failed:', recoveryError);
      window.location.reload();
    }
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div data-testid="error-boundary-fallback" className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-6 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Something went wrong
            </h2>
            <p className="text-muted-foreground mb-4">
              We're sorry! An unexpected error occurred.
            </p>
            <div className="bg-muted border border-border rounded-lg p-3 mb-6">
              <p className="text-sm text-muted-foreground">
                💡 Try "Try Again" first - it preserves your data
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Reload
              </button>
              
              <button
                onClick={this.handleReset}
                className="w-full bg-secondary text-secondary-foreground px-4 py-2 rounded-lg hover:bg-accent transition-colors"
              >
                Reset
              </button>
            </div>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  Error Details (Development)
                </summary>
                <div className="mt-2 p-3 bg-muted rounded text-xs overflow-auto">
                  <div className="font-semibold text-destructive mb-2">
                    {this.state.error.name}: {this.state.error.message}
                  </div>
                  <pre className="whitespace-pre-wrap text-muted-foreground">
                    {this.state.error.stack}
                  </pre>
                  {this.state.errorInfo && (
                    <div className="mt-2 pt-2 border-t border-border">
                      <div className="font-semibold text-foreground mb-1">Component Stack:</div>
                      <pre className="whitespace-pre-wrap text-muted-foreground text-xs">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}